import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, PartyPopper, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, GoldButton, Input, Select } from "@/components/ui-kit";

const GOAL_OPTIONS = [
  "Save money with exclusive student discounts and money-saving hacks",
  "Find a part-time job (and eventually build my career)",
  "Make new Indian friends and build my community",
  "Have one trusted point of contact whenever I need help in the UK",
  "All of the above",
];

const SOURCE_OPTIONS = ["Instagram", "Facebook", "Google", "Friend", "WhatsApp", "University", "Agent", "Other"];
const UK_CITIES = ["London", "Birmingham", "Manchester", "Leeds", "Leicester", "Coventry", "Nottingham", "Sheffield", "Liverpool", "Newcastle", "Glasgow", "Edinburgh", "Cardiff", "Bristol", "Other"];
const INDIAN_CITIES = ["Mumbai", "Delhi", "Bengaluru", "Hyderabad", "Chennai", "Kolkata", "Pune", "Ahmedabad", "Jaipur", "Kochi", "Chandigarh", "Indore", "Other"];

const arrivalMonths = () => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 12; i++) {
    out.push(d.toLocaleString("en-GB", { month: "long", year: "numeric" }));
    d.setMonth(d.getMonth() + 1);
  }
  return out;
};

const OptionCard = ({ selected, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`w-full text-left rounded-2xl border px-5 py-4 transition-all duration-300 ${
      selected ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white" : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-[#D4AF37]/40 hover:-translate-y-0.5"
    }`}
  >
    <span className="flex items-center justify-between gap-3">
      <span className="leading-relaxed">{children}</span>
      {selected && <Check className="w-5 h-5 text-[#D4AF37] shrink-0" />}
    </span>
  </button>
);

const TOTAL_STEPS = 8;

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state || {};
  const [searchParams] = useSearchParams();
  const isPackingCampaign = searchParams.get("campaign") === "packing";
  const sourceOptions = isPackingCampaign ? ["Packing Checklist", ...SOURCE_OPTIONS] : SOURCE_OPTIONS;
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: prefill.name || "",
    goals: [],
    whatsapp: "",
    whatsapp_country_code: "+91",
    email: prefill.email || "",
    password: "",
    university: "",
    course: "",
    uk_city: "",
    arrival_month: "",
    flight_date: "",
    indian_city: "",
    flight_booked: "",
    accommodation_booked: "",
    source: "",
    community_interest: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (isPackingCampaign) setForm((f) => (f.source ? f : { ...f, source: "Packing Checklist" }));
  }, [isPackingCampaign]);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        first_name: user.first_name || f.first_name,
        goals: user.goals?.length ? user.goals : f.goals,
        whatsapp: user.whatsapp || f.whatsapp,
        whatsapp_country_code: user.whatsapp_country_code || f.whatsapp_country_code,
        email: user.email || f.email,
        university: user.university || f.university,
        course: user.course || f.course,
        uk_city: user.uk_city || f.uk_city,
        arrival_month: user.arrival_month || f.arrival_month,
        flight_date: user.flight_date || f.flight_date,
        indian_city: user.indian_city || f.indian_city,
        flight_booked: user.flight_booked || f.flight_booked,
        accommodation_booked: user.accommodation_booked || f.accommodation_booked,
        source: user.source || f.source,
        community_interest: user.community_interest || f.community_interest,
      }));
      if (user.onboarding_completed) { navigate("/portal", { replace: true }); return; }
      const s = user.onboarding_step;
      if (s && s >= 1 && s < TOTAL_STEPS) setStep(s);
    }
  }, [user, navigate]);

  const startJourney = async () => {
    setSaving(true);
    try {
      if (!user) {
        const res = await api.post("/auth/start-onboarding", {});
        localStorage.setItem("cc_token", res.data.token);
        setUser(res.data.user);
      }
      setStep(1);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const saveStep = useCallback(async (nextStep, data) => {
    setSaving(true);
    try {
      const res = await api.patch("/onboarding", { step: nextStep, data });
      setUser(res.data.user);
      setStep(nextStep);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  }, [setUser]);

  const toggleGoal = (g) => {
    setForm((f) => {
      let goals;
      if (g === GOAL_OPTIONS[4]) {
        goals = f.goals.includes(g) ? [] : [GOAL_OPTIONS[4]];
      } else {
        goals = f.goals.includes(g) ? f.goals.filter((x) => x !== g) : [...f.goals.filter((x) => x !== GOAL_OPTIONS[4]), g];
      }
      return { ...f, goals };
    });
  };

  const next = async () => {
    if (step === 1) {
      if (!form.first_name.trim()) return toast.error("Please tell us your first name");
      if (!form.goals.length) return toast.error("Please pick at least one reason");
      await saveStep(2, { first_name: form.first_name.trim(), goals: form.goals });
    } else if (step === 2) {
      if (!form.whatsapp.trim()) return toast.error("Please enter your WhatsApp number");
      await saveStep(3, { whatsapp: form.whatsapp.trim(), whatsapp_country_code: form.whatsapp_country_code });
    } else if (step === 3) {
      if (!form.university.trim() || !form.uk_city || !form.arrival_month) return toast.error("Please fill university, city and arrival month");
      const data = {
        university: form.university.trim(), course: form.course.trim(), uk_city: form.uk_city,
        arrival_month: form.arrival_month, flight_date: form.flight_date,
      };
      if (!user?.has_password) {
        if (!form.email.trim() || form.password.length < 6) return toast.error("Please enter a valid email and a password (6+ characters)");
        try {
          setSaving(true);
          const res = await api.post("/auth/set-password", { email: form.email.trim(), password: form.password });
          setUser(res.data.user);
          toast.success("Verification code sent to your email");
        } catch (e) {
          toast.error(e.response?.data?.detail || "Could not save email");
          setSaving(false);
          return;
        }
        setSaving(false);
      }
      await saveStep(4, data);
    } else if (step === 4) {
      if (!form.indian_city) return toast.error("Please pick your city");
      await saveStep(5, { indian_city: form.indian_city });
    } else if (step === 5) {
      if (!form.flight_booked || !form.accommodation_booked) return toast.error("Please answer both questions");
      await saveStep(6, { flight_booked: form.flight_booked, accommodation_booked: form.accommodation_booked });
    } else if (step === 6) {
      if (!form.source) return toast.error("Please pick an option");
      await saveStep(7, { source: form.source });
    } else if (step === 7) {
      if (!form.community_interest) return toast.error("Please pick an option");
      await saveStep(8, { community_interest: form.community_interest });
    }
  };

  const progress = Math.round((step / TOTAL_STEPS) * 100);

  const stepContent = () => {
    switch (step) {
      case 0:
        return (
          <div data-testid="onboarding-welcome">
            <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-6">Welcome to Castles Creation</p>
            <h1 className="text-4xl sm:text-5xl text-white tracking-tight leading-tight">Built by students.<br />For students.</h1>
            <p className="mt-6 text-slate-400 text-lg leading-relaxed max-w-md">
              We're here to make your move to the UK simple, exciting and stress-free.
            </p>
            <GoldButton data-testid="start-journey-button" onClick={startJourney} disabled={saving} className="mt-10 text-base !px-9 !py-4">
              {saving ? "Starting..." : "Start My Journey"} <ArrowRight className="inline w-4 h-4 ml-1" />
            </GoldButton>
          </div>
        );
      case 1:
        return (
          <div data-testid="onboarding-step-1">
            <h2 className="text-2xl sm:text-3xl text-white tracking-tight">First, what's your first name?</h2>
            <div className="mt-6 max-w-md">
              <Input data-testid="first-name-input" placeholder="First name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} autoFocus />
            </div>
            {form.first_name.trim() && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
                <p className="text-slate-300 mb-4">Lovely to meet you, {form.first_name.trim()}. What's your main reason for joining? <span className="text-slate-500">(Pick all that apply)</span></p>
                <div className="space-y-3 max-w-xl">
                  {GOAL_OPTIONS.map((g, i) => (
                    <OptionCard key={g} testid={`goal-option-${i}`} selected={form.goals.includes(g)} onClick={() => toggleGoal(g)}>{g}</OptionCard>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        );
      case 2:
        return (
          <div data-testid="onboarding-step-2">
            <h2 className="text-2xl sm:text-3xl text-white tracking-tight">Your best WhatsApp number</h2>
            <p className="mt-3 text-slate-400 max-w-md leading-relaxed">We'll only use this to send important student updates, induction invites, city community links and support.</p>
            <div className="mt-7 flex gap-3 max-w-md">
              <div className="w-28">
                <Select data-testid="country-code-select" value={form.whatsapp_country_code} onChange={(e) => set("whatsapp_country_code", e.target.value)}>
                  <option value="+91">+91</option><option value="+44">+44</option><option value="+1">+1</option><option value="+971">+971</option>
                </Select>
              </div>
              <div className="flex-1">
                <Input data-testid="whatsapp-input" type="tel" placeholder="WhatsApp number" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} autoFocus />
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div data-testid="onboarding-step-3">
            <h2 className="text-2xl sm:text-3xl text-white tracking-tight">Your UK plans</h2>
            <div className="mt-7 grid sm:grid-cols-2 gap-5 max-w-2xl">
              {!user?.has_password && (
                <>
                  <Input data-testid="email-input" label="Email Address" type="email" placeholder="you@example.com" value={form.email} onChange={(e) => set("email", e.target.value)} />
                  <Input data-testid="password-input" label="Create a Password" type="password" placeholder="6+ characters" value={form.password} onChange={(e) => set("password", e.target.value)} />
                </>
              )}
              <Input data-testid="university-input" label="University" placeholder="e.g. University of Manchester" value={form.university} onChange={(e) => set("university", e.target.value)} />
              <Input data-testid="course-input" label="Course (optional)" placeholder="e.g. MSc Data Science" value={form.course} onChange={(e) => set("course", e.target.value)} />
              <Select data-testid="uk-city-select" label="UK City" value={form.uk_city} onChange={(e) => set("uk_city", e.target.value)}>
                <option value="">Select city</option>
                {UK_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
              <Select data-testid="arrival-month-select" label="Expected Arrival Month" value={form.arrival_month} onChange={(e) => set("arrival_month", e.target.value)}>
                <option value="">Select month</option>
                {arrivalMonths().map((m) => <option key={m} value={m}>{m}</option>)}
              </Select>
              <Input data-testid="flight-date-input" label="Expected Flight Date (optional)" type="date" value={form.flight_date} onChange={(e) => set("flight_date", e.target.value)} />
            </div>
          </div>
        );
      case 4:
        return (
          <div data-testid="onboarding-step-4">
            <h2 className="text-2xl sm:text-3xl text-white tracking-tight">Which city in India are you travelling from?</h2>
            <div className="mt-7 grid sm:grid-cols-2 gap-3 max-w-2xl">
              {INDIAN_CITIES.map((c) => (
                <OptionCard key={c} testid={`indian-city-${c.toLowerCase()}`} selected={form.indian_city === c} onClick={() => set("indian_city", c)}>{c}</OptionCard>
              ))}
            </div>
          </div>
        );
      case 5:
        return (
          <div data-testid="onboarding-step-5">
            <h2 className="text-2xl sm:text-3xl text-white tracking-tight">Almost there - two quick questions</h2>
            <div className="mt-8 max-w-xl space-y-8">
              <div>
                <p className="text-slate-300 mb-3">Have you booked your flight?</p>
                <div className="flex gap-3">
                  {["Yes", "No"].map((v) => (
                    <OptionCard key={v} testid={`flight-${v.toLowerCase()}`} selected={form.flight_booked === v} onClick={() => set("flight_booked", v)}>{v}</OptionCard>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-slate-300 mb-3">Have you secured accommodation?</p>
                <div className="flex gap-3">
                  {["Yes", "No"].map((v) => (
                    <OptionCard key={v} testid={`accommodation-${v.toLowerCase()}`} selected={form.accommodation_booked === v} onClick={() => set("accommodation_booked", v)}>{v}</OptionCard>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 6:
        return (
          <div data-testid="onboarding-step-6">
            <h2 className="text-2xl sm:text-3xl text-white tracking-tight">How did you hear about Castles?</h2>
            <div className="mt-7 grid sm:grid-cols-2 gap-3 max-w-2xl">
              {sourceOptions.map((s) => (
                <OptionCard key={s} testid={`source-${s.toLowerCase()}`} selected={form.source === s} onClick={() => set("source", s)}>{s}</OptionCard>
              ))}
            </div>
          </div>
        );
      case 7:
        return (
          <div data-testid="onboarding-step-7">
            <h2 className="text-2xl sm:text-3xl text-white tracking-tight max-w-xl leading-snug">
              Would you like us to introduce you to fellow Indian students moving to your city before you arrive?
            </h2>
            <div className="mt-7 space-y-3 max-w-xl">
              {["Yes definitely", "Maybe", "Not right now"].map((v) => (
                <OptionCard key={v} testid={`community-${v.toLowerCase().replace(/\s/g, "-")}`} selected={form.community_interest === v} onClick={() => set("community_interest", v)}>{v}</OptionCard>
              ))}
            </div>
          </div>
        );
      case 8:
        return (
          <div className="text-center" data-testid="onboarding-finish">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
              <PartyPopper className="w-9 h-9 text-[#D4AF37]" strokeWidth={1.6} />
            </div>
            <h1 className="mt-8 text-4xl sm:text-5xl text-white tracking-tight">Congratulations{form.first_name ? `, ${form.first_name}` : ""}!</h1>
            <p className="mt-5 text-lg text-slate-300">You're officially a Castles Member.</p>
            <p className="mt-2 text-slate-500 max-w-md mx-auto">Your UK journey now has a guide, a community and a plan. Welcome home.</p>
            <GoldButton data-testid="finish-journey-button" onClick={() => navigate(isPackingCampaign ? "/portal/packing" : "/portal")} className="mt-10 text-base !px-9 !py-4">
              {isPackingCampaign ? "Unlock My Packing Checklist" : "Start My Journey"} <ArrowRight className="inline w-4 h-4 ml-1" />
            </GoldButton>
            {!user?.email_verified && user?.has_password && (
              <button data-testid="verify-now-link" onClick={() => navigate("/verify-email")} className="block mx-auto mt-5 text-sm text-[#D4AF37] underline underline-offset-4">
                Verify your email now
              </button>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#060D1F] flex flex-col" data-testid="onboarding-page">
      <div className="px-5 sm:px-8 pt-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Logo />
          {step > 0 && step < TOTAL_STEPS && (
            <span className="text-xs tracking-[0.2em] uppercase text-slate-500" data-testid="step-indicator">Step {step} of 7</span>
          )}
        </div>
        {isPackingCampaign && (
          <div className="max-w-3xl mx-auto mt-5 rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.07] px-5 py-4" data-testid="packing-campaign-banner">
            <div className="flex items-center justify-between gap-4 text-xs font-semibold tracking-[0.12em] uppercase">
              <span className="text-[#D4AF37]">Step 1 of 2 · Create your free Castles account</span>
              <span className="text-slate-500 hidden sm:block">Step 2 of 2 · Unlock your packing checklist</span>
            </div>
            <p className="mt-2 text-sm text-slate-300">Sign up free and get your complete UK Student Packing Checklist.</p>
          </div>
        )}
      </div>
      {step > 0 && step < TOTAL_STEPS && (
        <div className="px-5 sm:px-8 mt-6">
          <div className="max-w-3xl mx-auto h-1.5 rounded-full bg-white/5 overflow-hidden" data-testid="progress-bar">
            <motion.div className="h-full bg-[#D4AF37] rounded-full" animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} />
          </div>
        </div>
      )}
      <main className="flex-1 flex items-center px-5 sm:px-8 py-14">
        <div className="max-w-3xl mx-auto w-full">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }}>
              {stepContent()}
            </motion.div>
          </AnimatePresence>
          {step > 0 && step < TOTAL_STEPS && (
            <div className="mt-10 flex items-center gap-4">
              <button data-testid="back-button" onClick={() => setStep(step - 1)} className="flex items-center gap-2 text-sm text-slate-400 transition-colors duration-300 hover:text-white">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <GoldButton data-testid="continue-button" onClick={next} disabled={saving}>
                {saving ? "Saving..." : "Continue"} <ArrowRight className="inline w-4 h-4 ml-1" />
              </GoldButton>
              <span className="text-xs text-slate-600 ml-auto hidden sm:block">Saved automatically as you go</span>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
